import React from "react";
import { Plus, Trash2 } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Editor for ExtractorField.schema_definition — the nested property tree that
// backs "object" and "table" data types. Mirrors the backend's node shape
// exactly (extractor_studio/serializers.py _validate_schema_node):
//   { type: "object", properties: [ {key,type,description,...}, ... ] }
//   { type: "table",  merge_key?, items: { type: "object", properties: [...] } }
// A property node can itself be type "object"/"table", nesting recursively.
// ─────────────────────────────────────────────────────────────────────────────

const PROPERTY_TYPES = ["text", "number", "date", "object", "table"];
const isContainerType = (t) => t === "object" || t === "table";

const emptyProperty = () => ({ key: "", type: "text", description: "" });

const rowInput =
  "px-2.5 py-1.5 border border-[#ECE9EE] rounded-[7px] bg-white text-[12.5px] text-[#1A1A1A] " +
  "outline-none transition-colors placeholder:text-[#8A8A90] focus:border-[#A04A97]";

/* Renders the editable property list for a container node — "properties" for
   object nodes, "items.properties" (+ merge_key) for table nodes. Used both
   at the field's top level and recursively inside a nested object/table
   property, so `node` may or may not carry a "key" (top level never does). */
const SchemaPropertiesList = ({ node, onChange }) => {
  const isTable = node.type === "table";
  const items = isTable ? (node.items || { type: "object", properties: [] }) : node;
  const properties = items.properties || [];

  const setProperties = (newProps) => {
    if (isTable) onChange({ ...node, items: { ...items, type: "object", properties: newProps } });
    else onChange({ ...node, properties: newProps });
  };

  const addProperty = () => setProperties([...properties, emptyProperty()]);
  const updateProperty = (idx, prop) => setProperties(properties.map((p, i) => (i === idx ? prop : p)));
  const removeProperty = (idx) => setProperties(properties.filter((_, i) => i !== idx));

  return (
    <div className="flex flex-col gap-2">
      {isTable && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10.5px] font-semibold text-[#8A8A90] uppercase tracking-[0.05em] flex-shrink-0">Merge Key</span>
          <input
            value={node.merge_key || ""}
            onChange={(e) => onChange({ ...node, merge_key: e.target.value })}
            placeholder="e.g. item_id — dedupes rows carried over from batched pages"
            className={`${rowInput} flex-1 font-mono`}
          />
        </div>
      )}
      {properties.length === 0 && (
        <div className="text-[12px] text-[#8A8A90] italic py-1.5">No properties yet — add one below.</div>
      )}
      {properties.map((prop, idx) => (
        <SchemaPropertyRow
          key={idx}
          property={prop}
          onChange={(p) => updateProperty(idx, p)}
          onDelete={() => removeProperty(idx)}
        />
      ))}
      <button
        type="button"
        onClick={addProperty}
        className="self-start flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] border border-dashed border-[#E9D2E6] text-[#893F82] text-[11.5px] font-semibold hover:bg-[#FBF5FA] transition-colors"
      >
        <Plus size={12} /> Add property
      </button>
    </div>
  );
};

const SchemaPropertyRow = ({ property, onChange, onDelete }) => {
  const type = property.type || "text";
  const container = isContainerType(type);

  const changeType = (newType) => {
    const base = { key: property.key, type: newType, description: property.description };
    if (newType === "object") base.properties = property.properties || [];
    if (newType === "table") {
      base.merge_key = property.merge_key || "";
      base.items = property.items || { type: "object", properties: [] };
    }
    onChange(base);
  };

  return (
    <div className="border border-[#ECE9EE] rounded-[10px] p-3 bg-[#FAF9FB]">
      <div className="flex items-center gap-2">
        <input
          value={property.key || ""}
          onChange={(e) => onChange({ ...property, key: e.target.value })}
          placeholder="property_key"
          className={`${rowInput} flex-1 font-mono`}
        />
        <select
          value={type}
          onChange={(e) => changeType(e.target.value)}
          className={`${rowInput} cursor-pointer flex-shrink-0`}
        >
          {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          type="button" onClick={onDelete} title="Remove property"
          className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-[7px] text-[#B33A1E] hover:bg-[#FCEDED] transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <input
        value={property.description || ""}
        onChange={(e) => onChange({ ...property, description: e.target.value })}
        placeholder="Description (optional) — helps the AI locate this value"
        className={`${rowInput} w-full mt-2 text-[#52525B]`}
      />
      {container && (
        <div className="mt-3 pl-3 border-l-2 border-[#E9D2E6]">
          <SchemaPropertiesList node={property} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

// Default empty node for a field switching into "object"/"table" with no
// schema_definition yet — matches what the backend normalizes bare {} into.
export const emptySchemaFor = (dataType) =>
  dataType === "table"
    ? { type: "table", items: { type: "object", properties: [] } }
    : dataType === "object"
      ? { type: "object", properties: [] }
      : null;

/* Top-level entry point — `dataType` is the parent field's data_type (object
   or table only; callers should not render this for scalar types). */
const SchemaBuilder = ({ dataType, schema, onChange }) => {
  const node = schema && schema.type ? schema : emptySchemaFor(dataType);
  if (!node) return null;
  return (
    <div className="p-3 border border-[#ECE9EE] rounded-[11px] bg-white">
      <SchemaPropertiesList node={node} onChange={onChange} />
    </div>
  );
};

export default SchemaBuilder;
